import { useEffect, useRef, useState } from "react";
import { Outlet, useLocation } from "react-router-dom";
import { useAuth } from "@/auth/AuthProvider";
import { ProductLineSwitcher } from "@/components/ProductLineSwitcher";
import { SideNav, SIDEBAR_ID } from "@/components/SideNav";
import { UserMenu } from "@/components/UserMenu";
import { NotificationBell } from "@/components/NotificationBell";
import { HeaderSearch } from "@/components/HeaderSearch";
import { TopBarTrail } from "@/components/TopBarTrail";
import { ProgressProvider, TopProgressBar } from "@/components/TopProgress";
import { PageTrailProvider } from "@/components/PageTrail";
import { SiteFooter } from "@/components/SiteFooter";
import { PageScrollCue } from "@/components/ScrollCue";
import { useSupportChat } from "@/components/SupportChatProvider";
import { PageErrorBoundary } from "@/components/PageErrorBoundary";
import { useProductLine } from "@/platform/ProductLineProvider";
import {
  isExchangePath,
  isWidePath,
  PRODUCT_LINE_HOME,
} from "@/platform/productLine";
import {
  readSidebarCollapsed,
  writeSidebarCollapsed,
} from "@/lib/devicePreferences";
import {
  getOwnProfile,
  getOwnBrokerage,
  hasCapability,
  type BrokerProfile,
  type BrokerBrokerage,
} from "@/lib/brokerAuth";
import { TOUCH_SQUARE } from "@/components/buttonStyles";

// The authenticated app shell (BRK-S035): a left nav carrying the primary
// destinations, and a top bar whose right side holds the notification bell and
// the user menu (the broker's first name; BRK-S242). Support has one home, the
// sidebar's bottom group: the top bar's Help and the footer's Contact Support
// went with BRK-S258. Mounted
// as a layout route around the whole protected group, so it also wraps the
// security-onboarding gate — an authenticated broker always sees this chrome;
// the gate only swaps <main>.
//
// BrokerIdentity (from /me) has no display name, so the shell reads the broker's
// name from GET /api/broker/profile and the brokerage name from
// GET /api/broker/brokerage. Both reads fail soft (never white-screen, #7): the
// name falls back to the email identity, the brokerage to a neutral label.
//
// Mobile-first: below lg the sidebar is an off-canvas drawer toggled by the
// header hamburger; at lg+ it's a persistent, full-height column topped by the
// Signal logo (BRK-S258), with the top bar, the page and the footer to its
// right. It collapses to an icon rail (BRK-S244), remembered on this device (a
// documented CLAUDE.md #9 exception).
/** A trimmed profile name, or null when it is missing or blank. */
function nameField(value: unknown): string | null {
  return typeof value === "string" && value.trim() ? value.trim() : null;
}

export function PortalShell() {
  const { identity, authedRequest } = useAuth();
  const [profile, setProfile] = useState<BrokerProfile | null>(null);
  const [brokerage, setBrokerage] = useState<BrokerBrokerage | null>(null);
  const [navOpen, setNavOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(readSidebarCollapsed);
  // The scroll cue scrolls by the band between this header and itself.
  const headerRef = useRef<HTMLElement>(null);
  // The Exchange landing owns a full-bleed dark canvas; every other working
  // screen keeps the light, padded content column.
  const pathname = useLocation().pathname;
  // Full-bleed, self-padded landing pages (own their own bg + padding) — the
  // list of them is owned with the rest of the route→platform knowledge.
  const isExchange = isExchangePath(pathname);
  // Lists fill the content area; forms and records run to the form width
  // (BRK-S262).
  const isWide = isWidePath(pathname);
  const canViewAccounts = hasCapability(identity, "account:view");
  const { describeBroker } = useSupportChat();
  // A page that fails to render offers a way back to this platform's home.
  const { productLine } = useProductLine();

  useEffect(() => {
    let active = true;
    getOwnProfile(authedRequest)
      .then((p) => {
        if (active) setProfile(p);
      })
      .catch(() => {
        /* fail soft — fall back to the email identity */
      });
    getOwnBrokerage(authedRequest)
      .then((b) => {
        if (active) setBrokerage(b);
      })
      .catch(() => {
        /* fail soft — fall back to the neutral label */
      });
    return () => {
      active = false;
    };
  }, [authedRequest]);

  // Resolved-or-null first, then the display fallbacks. Live chat wants to know
  // the difference: a real name identifies the broker to the agent, whereas the
  // fallbacks ("Your brokerage", the raw email) are chrome, and shipping a
  // placeholder as an identity would tell the agent something untrue.
  //
  // The profile read isn't narrowed at runtime, so a name field PMS leaves out
  // counts as missing rather than being read as a string.
  const firstName = nameField(profile?.firstName);
  const resolvedName =
    [firstName, nameField(profile?.lastName)].filter(Boolean).join(" ") || null;
  const resolvedBrokerage = brokerage?.name ?? null;
  const displayName = resolvedName ?? identity?.email ?? "";
  const brokerageName = resolvedBrokerage ?? "Your brokerage";
  const initials = displayName
    .split(/\s+/)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() ?? "")
    .join("");

  function toggleCollapsed() {
    const next = !collapsed;
    setCollapsed(next);
    writeSidebarCollapsed(next);
  }

  // Hand the support widget the names this shell already read (BRK-S136), so an
  // agent opens the conversation knowing who is asking without a second fetch.
  useEffect(() => {
    describeBroker({
      displayName: resolvedName,
      brokerageName: resolvedBrokerage,
    });
  }, [describeBroker, resolvedName, resolvedBrokerage]);

  // The routed page, inside the boundary that keeps a render throw from taking
  // this chrome down with it (BRK-S032).
  const page = (
    <PageErrorBoundary
      homeTo={PRODUCT_LINE_HOME[productLine]}
      homeLabel="Back to the portal"
    >
      <Outlet />
    </PageErrorBoundary>
  );

  return (
    // The provider lets a page name itself in the top bar's breadcrumb
    // (usePageTrailName; BRK-S269), so it spans the header and the page; the
    // progress provider likewise lets a submission run the top bar's one
    // global progress bar (useProgress; BRK-S488).
    <PageTrailProvider>
      <ProgressProvider>
        <div
          data-portal-shell
          className="flex min-h-full bg-brk-bg text-brk-text"
        >
          {/* Dimmed overlay behind the mobile drawer (below lg only). */}
          {navOpen && (
            <button
              type="button"
              aria-hidden="true"
              tabIndex={-1}
              onClick={() => setNavOpen(false)}
              className="fixed inset-0 z-30 bg-brk-ink/40 lg:hidden"
            />
          )}

          {/* At lg+ the sidebar runs the full height (BRK-S258), sticky while the
          page scrolls, and carries the Signal logo in its top block. */}
          <aside
            id={SIDEBAR_ID}
            className={`fixed inset-y-0 left-0 z-40 w-64 border-r border-brk-border bg-brk-panel transition-[transform,width] duration-200 motion-reduce:transition-none lg:sticky lg:top-0 lg:z-auto lg:h-dvh lg:shrink-0 lg:self-start ${
              navOpen ? "translate-x-0" : "-translate-x-full"
            } lg:translate-x-0 ${collapsed ? "lg:w-16" : "lg:w-64"}`}
          >
            <SideNav
              identity={identity}
              onNavigate={() => setNavOpen(false)}
              collapsed={collapsed}
              onToggleCollapsed={toggleCollapsed}
              platformSwitcher={navOpen}
            />
          </aside>

          <div className="flex min-w-0 flex-1 flex-col">
            {/* The header and, at lg+, the footer are fixed chrome (BRK-S228/S229),
          sized by --brk-header-h and --brk-footer-h (index.css), in the column
          to the sidebar's right. The header is the same 56px at every width
          (BRK-S262). They wear the theme's surface (BRK-S247): white with Ink
          type by day, Ink with Paper type by night, since brk-panel/brk-text
          flip. */}
            <header
              ref={headerRef}
              className="sticky top-0 z-30 h-[var(--brk-header-h)] border-b border-brk-border bg-brk-panel text-brk-text"
            >
              <div className="flex h-full items-center justify-between gap-3 px-4 sm:px-6">
                <div className="flex min-w-0 items-center gap-2">
                  <button
                    type="button"
                    aria-label={
                      navOpen ? "Close navigation" : "Open navigation"
                    }
                    aria-expanded={navOpen}
                    aria-controls={SIDEBAR_ID}
                    onClick={() => setNavOpen((prev) => !prev)}
                    className={`-ml-1 inline-flex items-center justify-center rounded-md p-2 text-brk-text transition-colors hover:bg-brk-bg lg:hidden ${TOUCH_SQUARE}`}
                  >
                    <svg
                      width="22"
                      height="22"
                      viewBox="0 0 24 24"
                      fill="none"
                      aria-hidden="true"
                    >
                      <path
                        d="M4 7h16M4 12h16M4 17h16"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                      />
                    </svg>
                  </button>
                  {/* The page's breadcrumb, the portal's one trail (BRK-S269). It
                took the logo's place: the logo tops the sidebar at lg+ and the
                drawer below it (BRK-S258). */}
                  <TopBarTrail />
                </div>
                {/* The search keeps room for its placeholder, so a long breadcrumb
                truncates before it squeezes the search (BRK-S269). */}
                {canViewAccounts && (
                  <div className="hidden min-w-48 flex-1 justify-center px-4 md:flex">
                    <HeaderSearch />
                  </div>
                )}
                <div
                  data-top-bar-end
                  className="flex shrink-0 items-center gap-2 sm:gap-3"
                >
                  {/* The platform switch sits beside the bell (BRK-S269). Below sm
                it moves into the drawer, so the row fits a phone (BRK-S248). */}
                  <ProductLineSwitcher className="hidden sm:block" />
                  {/* In-app inbox (BRK-S157): a real UNSEEN badge over a link to
                /notifications, gated on notification:view. */}
                  <NotificationBell />
                  <UserMenu
                    firstName={firstName}
                    fullName={resolvedName}
                    email={identity?.email ?? ""}
                    initials={initials}
                    brokerageName={brokerageName}
                  />
                </div>
              </div>
              {/* The submission's global progress bar, on this bar's bottom
              edge (BRK-S488). */}
              <TopProgressBar />
            </header>

            <div className="flex flex-1">
              {/* Each <main> ends with the "more below" cue (BRK-S240), a down
            arrow that shows while the page runs on past the visible area. */}
              {isExchange ? (
                // The Exchange landings own a full-width canvas, and pad it as main
                // pads every other page (BRK-S262).
                <main className="min-w-0 flex-1 bg-brk-bg">
                  {page}
                  <PageScrollCue headerRef={headerRef} />
                </main>
              ) : (
                // Main pads 16px on a phone and 24px from sm, and pages add none of
                // their own, so every page's header sits the same distance under
                // the top bar (BRK-S262).
                <main className="min-w-0 flex-1 p-4 sm:p-6">
                  {/* The page column: a list stretches it to main's height, so its
                pager sits on the bottom edge (BRK-S250, index.css). */}
                  <div
                    data-page-column
                    className={`mx-auto w-full ${isWide ? "max-w-none" : "max-w-form"}`}
                  >
                    {page}
                  </div>
                  <PageScrollCue headerRef={headerRef} />
                </main>
              )}
            </div>

            <SiteFooter sticky contactSupport={false} />
          </div>
        </div>
      </ProgressProvider>
    </PageTrailProvider>
  );
}
